import React from 'react';
import { Button, Form, Alert } from 'react-bootstrap';
import { GoStack, GoListUnordered } from 'react-icons/go';
import { LuTarget } from 'react-icons/lu';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-markdown';
import 'ace-builds/src-noconflict/theme-monokai';
import Tooltip from '../../../../controls/common/tooltip';
import WorkNodeBase from '../common/workNodeBase';
import CellsSurface from '../surfaces/cells/cellsSurface';
import Dialog from '../components/modal';
import WidgetStatus from '../../common/widgetStatus';
import {
  createStore,
  setMissions,
  setCells,
  setDependsOn,
  setTemplates,
  setHeartbeat,
  listMissions,
  listCells,
  upsertCell,
} from '../domain/model';
import { missionAlias } from '../domain/schema';
import MissionSwitcher from '../components/missionSwitcher';

/**
 * MissionsNode — the Missions widget (`system.work.missions`).
 *
 * This is the former Mission Control container MINUS the tab shell: it owns the
 * mission store, the soft + serialized reload(), the session-content listener,
 * and applyLocal, and renders the cells surface (CellsSurface) directly — no
 * surface tabs.
 *
 * It also absorbs the mission-scoped controls that used to live in the tabbed
 * shell's top bar:
 *   - "New Mission" / "New Cell" are now widget-header buttons (renderHeaderActions),
 *     opening the New-Mission / Add-Cell modals owned here.
 *   - The mission selector moved INTO the Mission panel (surfaces/cells/missionPanel.js),
 *     driven by CellsSurface from the store — no top bar at all.
 *   - The heartbeat / online-offline indicator was removed.
 *
 * Data flow (unchanged from the former container):
 *   - On mount: construct client (via base), run reload()
 *   - On session-content change: run reload() (serialized + soft)
 *   - On session change (componentDidUpdate): run reload()
 */
class MissionsNode extends WorkNodeBase {
  constructor(props) {
    super(props);
    this.state = {
      store: createStore(),
      selection: null,
      loading: false,
      error: null,

      // Mission-switcher popup, opened from the widget-header button.
      showMissionSwitcher: false,

      // New-mission modal state (migrated from the former shell.js)
      showNewMission: false,
      newMissionTitle: '',
      newMissionDescription: '',
      missionBusy: false,
      missionError: null,

      // Add-cell modal state (migrated from the former shell.js)
      showAddCell: false,
      addCellTitle: '',
      addCellDescription: '',
      addCellBusy: false,
      addCellError: null,
    };
    this._lastSessionId = null;

    // Stable handler identities — bound once so the CellsSurface → panel prop chain
    // stays referentially stable across renders (keeps the mission graph's memoised
    // nodes/edges from churning on unrelated re-renders).
    this.applyLocal = this.applyLocal.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.handleReload = this.handleReload.bind(this);
  }

  // ---------------------------------------------------------------------------
  // Stable handlers passed down to CellsSurface
  // ---------------------------------------------------------------------------

  /**
   * Apply a pure store updater locally without a server round-trip. Used by every
   * in-place write (add/remove/move cell, add/remove dependency, edit props) so
   * the graph updates incrementally — never a full reload + re-layout.
   * @param {(store: Object) => Object} updater
   */
  applyLocal(updater) {
    if (this._mounted) {
      this.setState(s => ({ store: updater(s.store) }));
    }
  }

  handleSelect(item) {
    this.setState({ selection: item });
  }

  handleReload() {
    this.reload();
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  componentDidMount() {
    // Base builds the client (getClient) and sets up the WidgetBase forceUpdate listener.
    super.componentDidMount();
    this._lastSessionId = this._getSessionId();

    // Re-run reload when session objects finish loading. The base class registers a
    // forceUpdate listener for rendering; we add a separate one here so that when session
    // content changes (e.g. session objects arrive asynchronously on a fresh page load),
    // the mission store is rebuilt from the fresh data rather than staying empty.
    this._sessionContentListener = () => {
      if (!this._mounted) return;
      // If a reload is already in flight, mark dirty — reload() re-runs once when it finishes. Guarding
      // on _reloading (not state.loading) keeps the spinner-less soft reload from running concurrently.
      if (this._reloading) { this._reloadDirty = true; return; }
      this.reload();
    };
    if (this.context) {
      this.context.addListener(this._sessionContentListener);
    }

    this.reload();
  }

  componentDidUpdate() {
    super.componentDidUpdate();
    // Detect session change and re-fetch when it does.
    const sessionId = this._getSessionId();
    if (sessionId !== this._lastSessionId) {
      this._lastSessionId = sessionId;
      this.reload();
    }
  }

  componentWillUnmount() {
    if (this.context && this._sessionContentListener) {
      this.context.removeListener(this._sessionContentListener);
    }
    this._sessionContentListener = null;
    super.componentWillUnmount();
  }

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  async reload() {
    if (!this._mounted) return;
    if (!this.context || !this.context.session) {
      // No session — reset to empty store, no error
      this.setState({ store: createStore(), loading: false, error: null });
      return;
    }

    const client = this._client;

    // Only show the loading spinner on the FIRST load. Once the store has missions, a refresh — e.g.
    // the session-content listener firing after we uploaded an edited mission's metadata — updates the
    // store in place WITHOUT unmounting the surface, so the mission graph keeps its layout, selection,
    // and viewport instead of remounting + re-fitting (the "mission edit reloads the graph" bug).
    const hasData = Object.keys((this.state.store && this.state.store.missionsById) || {}).length > 0;
    this.setState({ loading: !hasData, error: null });

    // Serialize reloads. The session-content listener fires repeatedly as objects arrive; without this
    // guard the soft (spinner-less) reload would run several reloads CONCURRENTLY and a stale one — read
    // before templates finished loading — could land last and wipe the store ("workflows not loading").
    // A change arriving mid-reload sets _reloadDirty so the final state is re-fetched exactly once.
    this._reloading = true;
    this._reloadDirty = false;

    try {
      // Load missions, templates, heartbeat in parallel
      const [missions, templates, heartbeat] = await Promise.all([
        client.listMissions(),
        client.getTemplates(),
        client.getHeartbeat(),
      ]);

      if (!this._mounted) return;

      // Build store incrementally
      let store = createStore();
      store = setMissions(store, missions || []);
      store = setTemplates(store, templates || { workflows: {}, workspaces: {} });
      store = setHeartbeat(store, heartbeat || null);

      // For each mission, load its cells and dependency edges
      const missionIds = (missions || []).map(m => m.id);
      const [cellResults, edgeResults] = await Promise.all([
        Promise.all(missionIds.map(id => client.listCells(id).then(cells => ({ id, cells })))),
        Promise.all(missionIds.map(id => client.listDependsOn(id).then(edges => ({ id, edges }))))
      ]);

      if (!this._mounted) return;

      for (const { id, cells } of cellResults) {
        store = setCells(store, id, cells || []);
      }
      for (const { id, edges } of edgeResults) {
        store = setDependsOn(store, id, edges || []);
      }

      this.setState({ store, loading: false, error: null }, () => this._ensureMissionSelected());
    } catch (err) {
      if (this._mounted) {
        this.setState({
          loading: false,
          error: err && err.message ? err.message : 'Failed to load Mission Control data.',
        });
      }
    } finally {
      this._reloading = false;
      // A content change arrived while we were loading — re-fetch once to converge on the latest state.
      if (this._mounted && this._reloadDirty) {
        this._reloadDirty = false;
        this.reload();
      }
    }
  }

  /**
   * Default the active mission to the first one once missions load and nothing valid is
   * selected (formerly the tabbed shell's missionIdsSig effect). Creating or switching
   * missions never re-runs this when the current selection is still valid.
   */
  _ensureMissionSelected() {
    if (!this._mounted) return;
    const missions = listMissions(this.state.store);
    if (!missions.length) return;
    const current = this.state.selection && this.state.selection.missionId;
    if (!current || !missions.some(m => m.id === current)) {
      this.handleSelect({ missionId: missions[0].id, cellId: null, edge: null });
    }
  }

  // ---------------------------------------------------------------------------
  // Mission switcher (widget-header button → popup with a single Select)
  // ---------------------------------------------------------------------------

  openMissionSwitcher = () => {
    this.setState({ showMissionSwitcher: true });
  };

  // Apply the picked mission and close the popup (no confirm button — selecting IS the action).
  handleSwitchMission = (missionId) => {
    if (missionId) this.handleSelect({ missionId, cellId: null, edge: null });
    this.setState({ showMissionSwitcher: false });
  };

  // ---------------------------------------------------------------------------
  // New-mission modal (migrated from the former shell.js)
  // ---------------------------------------------------------------------------

  openNewMission = () => {
    this.setState({
      showNewMission: true,
      newMissionTitle: '',
      newMissionDescription: '',
      missionError: null,
    });
  };

  handleCreateMission = async () => {
    const { newMissionTitle, newMissionDescription } = this.state;
    if (!newMissionTitle.trim() || !this._client) return;
    this.setState({ missionBusy: true, missionError: null });
    try {
      const id = 'm_' + Date.now();
      await this._client.createMission({
        id,
        title: newMissionTitle.trim(),
        description: newMissionDescription.trim(),
      });
      this.setState({ showNewMission: false });
      this.reload();
    } catch (e) {
      this.setState({ missionError: e && e.message ? e.message : 'Failed to create mission.' });
    } finally {
      this.setState({ missionBusy: false });
    }
  };

  // ---------------------------------------------------------------------------
  // Add-cell modal (migrated from the former shell.js)
  // ---------------------------------------------------------------------------

  openAddCell = () => {
    this.setState({
      showAddCell: true,
      addCellTitle: '',
      addCellDescription: '',
      addCellError: null,
    });
  };

  handleAddCell = async () => {
    const { selection, addCellTitle, addCellDescription } = this.state;
    const missionId = selection && selection.missionId;
    if (!addCellTitle.trim() || !missionId || !this._client) return;
    this.setState({ addCellBusy: true, addCellError: null });
    try {
      // Root cells (added here, not via the graph) default both structural modes to inherit-from-mission.
      const cell = await this._client.addCell(missionId, {
        title: addCellTitle.trim(),
        description: addCellDescription,
        workspace_mode: 'inherit_from_mission',
        cell_workflow_mode: 'inherit_from_mission',
      });
      this.setState({ showAddCell: false });
      // Incremental insert — no full reload / re-layout.
      this.applyLocal(s => upsertCell(s, missionId, cell));
    } catch (e) {
      this.setState({ addCellError: e && e.message ? e.message : 'Failed to add cell.' });
    } finally {
      this.setState({ addCellBusy: false });
    }
  };

  // ---------------------------------------------------------------------------
  // WidgetBase overrides
  // ---------------------------------------------------------------------------

  getClassName() {
    return 'playground-work-node playground-missions-node';
  }

  // The selected mission shown in the header, styled exactly like the graph/thread widgets'
  // bound-object alias (its session-object alias `missions/<mid>/graph`).
  renderHeaderAlias() {
    const missionId = this.state.selection && this.state.selection.missionId;
    if (!missionId) return null;
    return <span className="playground-widget-alias">{missionAlias(missionId)}</span>;
  }

  // Header actions: Switch mission (when any exist) + New Mission (always) + New Cell (when a mission
  // is selected). These replace the former top-bar controls; follows the graph widget's header pattern.
  renderHeaderActions() {
    const { selection } = this.state;
    const hasMissionSelected = !!(selection && selection.missionId);
    const hasMissions = listMissions(this.state.store).length > 0;
    return (
      <span className="playground-widget-controls" onClick={(e) => e.stopPropagation()}>
        {hasMissions && (
          <Tooltip text="Switch mission">
            <Button variant="light" size="sm" onClick={this.openMissionSwitcher} aria-label="Switch mission">
              <GoListUnordered />
            </Button>
          </Tooltip>
        )}
        <Tooltip text="New mission">
          <Button variant="light" size="sm" onClick={this.openNewMission} aria-label="New mission">
            <LuTarget />
          </Button>
        </Tooltip>
        {hasMissionSelected && (
          <Tooltip text="New cell">
            <Button variant="light" size="sm" onClick={this.openAddCell} aria-label="New cell">
              <GoStack />
            </Button>
          </Tooltip>
        )}
      </span>
    );
  }

  renderBody() {
    const {
      store, selection, loading, error, showMissionSwitcher,
      showNewMission, newMissionTitle, newMissionDescription, missionBusy, missionError,
      showAddCell, addCellTitle, addCellDescription, addCellBusy, addCellError,
    } = this.state;

    // Mission-switcher popup rows (title + cell count) and the active id.
    const missionRows = listMissions(store).map(m => ({
      id: m.id,
      title: m.title,
      cellCount: listCells(store, m.id).length,
    }));
    const selectedMissionId = selection && selection.missionId;

    return (
      <div className="mission-control-shell">
        {/* Mission switcher popup (widget-header "Switch mission" button) — a Select Session-style
            table; picking a mission applies it and closes (handleSwitchMission). */}
        <MissionSwitcher
          show={showMissionSwitcher}
          missions={missionRows}
          currentId={selectedMissionId}
          onHide={() => this.setState({ showMissionSwitcher: false })}
          onSelect={this.handleSwitchMission}
        />

        <div className="mission-control-surface-body">
          {loading && (
            <WidgetStatus variant="primary" loading>Loading…</WidgetStatus>
          )}
          {!loading && error && (
            <WidgetStatus variant="warning">{error}</WidgetStatus>
          )}
          {!loading && !error && (
            <CellsSurface
              store={store}
              client={this._client}
              selection={selection}
              onSelect={this.handleSelect}
              onReload={this.handleReload}
              onApplyLocal={this.applyLocal}
            />
          )}
        </div>

        {/* New mission modal — required Title + markdown Description */}
        <Dialog
          show={showNewMission}
          title="New Mission"
          onHide={() => this.setState({ showNewMission: false })}
          onSubmit={this.handleCreateMission}
          submitLabel={missionBusy ? 'Creating…' : 'Create'}
          submitDisabled={!newMissionTitle.trim() || missionBusy}
        >
          <Form.Group style={{ marginBottom: 8 }}>
            <Form.Label><strong>Title</strong> <span className="text-muted">(required)</span></Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={newMissionTitle}
              onChange={e => this.setState({ newMissionTitle: e.target.value })}
              placeholder="Mission title…"
              autoFocus
            />
            <Form.Text muted>
              A short, descriptive name for this mission.
            </Form.Text>
          </Form.Group>
          <Form.Group style={{ marginBottom: 0 }}>
            <Form.Label><strong>Description</strong></Form.Label>
            <AceEditor
              mode="markdown"
              theme="monokai"
              width="100%"
              wrapEnabled
              minLines={4}
              maxLines={12}
              value={newMissionDescription}
              onChange={value => this.setState({ newMissionDescription: value })}
            />
            <Form.Text muted>
              Optional notes or context for this mission. Supports Markdown.
            </Form.Text>
          </Form.Group>
          {missionError && (
            <Alert variant="warning" style={{ marginTop: 8, fontSize: '0.8rem', marginBottom: 0 }}>
              {missionError}
            </Alert>
          )}
        </Dialog>

        {/* Add cell modal — required Title + markdown Description + optional Cell Workflow */}
        <Dialog
          show={showAddCell}
          title="Add Cell"
          onHide={() => this.setState({ showAddCell: false })}
          onSubmit={this.handleAddCell}
          submitLabel={addCellBusy ? 'Adding…' : 'Add Cell'}
          submitDisabled={!addCellTitle.trim() || addCellBusy}
        >
          <Form.Group style={{ marginBottom: 8 }}>
            <Form.Label><strong>Title</strong> <span className="text-muted">(required)</span></Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={addCellTitle}
              onChange={e => this.setState({ addCellTitle: e.target.value })}
              placeholder="Cell title…"
              autoFocus
            />
            <Form.Text muted>
              A short, descriptive name for this cell.
            </Form.Text>
          </Form.Group>
          <Form.Group style={{ marginBottom: 0 }}>
            <Form.Label><strong>Description</strong></Form.Label>
            <AceEditor
              mode="markdown"
              theme="monokai"
              width="100%"
              wrapEnabled
              minLines={4}
              maxLines={12}
              value={addCellDescription}
              onChange={value => this.setState({ addCellDescription: value })}
            />
            <Form.Text muted>
              Optional notes or context for this cell. Supports Markdown.
            </Form.Text>
          </Form.Group>
          {addCellError && (
            <Alert variant="warning" style={{ marginTop: 8, fontSize: '0.8rem', marginBottom: 0 }}>
              {addCellError}
            </Alert>
          )}
        </Dialog>
      </div>
    );
  }
}

export default MissionsNode;
